/**
 * Host for the full-screen read-only artifact viewer.
 *
 * Mirrors LightboxRoot: a leaf store decides visibility and this renders
 * nothing until something opens. Mounted alongside LightboxRoot in every
 * layout, but only ever opened on clients without the desktop-web editors
 * (see `openMessageFile`), which is why the chrome points at desktop for
 * editing rather than offering edit affordances here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BackHandler, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { useArtifactViewerStore } from '@/store/artifactViewer';
import { openFileExternally } from '@/constants/utils/files';
import { codeChangeStats } from '@/constants/utils/code_diff';
import SlideDeckViewer from '@/components/Files/SlideDeckViewer';
import PptxDeckViewer from '@/components/Files/PptxDeckViewer';
import WorkbookViewer from './WorkbookViewer';
import DocumentViewer from '@/components/Files/DocumentViewer';
import CodeViewer from '@/components/Files/CodeViewer';
import PdfViewer from '@/components/Files/PdfViewer';
import VideoPreviewPanel from '@/components/Files/VideoPreviewPanel';
import ArtifactDownloadButton from '@/components/Files/ArtifactDownloadButton';
import { prepareArtifactDownload } from '@/lib/artifactDownload';
import useMacModalFocus from '@/hooks/useMacModalFocus';
import { getWindowOverlayTopInset } from '@/lib/macTitlebar';

const KIND_LABELS = {
  presentation: { fallbackTitle: 'Presentation', open: 'Open deck' },
  pptx: { fallbackTitle: 'Presentation', open: 'Open deck' },
  spreadsheet: { fallbackTitle: 'Spreadsheet', open: 'Open workbook' },
  document: { fallbackTitle: 'Document', open: 'Open document' },
  code: { fallbackTitle: 'Code', open: 'Open file' },
  pdf: { fallbackTitle: 'PDF', open: 'Open PDF in another app', subtitle: 'PDF · Preview' },
};

const KIND_VIEWERS = {
  presentation: SlideDeckViewer,
  pptx: PptxDeckViewer,
  spreadsheet: WorkbookViewer,
  document: DocumentViewer,
  code: CodeViewer,
  pdf: PdfViewer,
};

export default function ArtifactViewerRoot() {
  const isOpen = useArtifactViewerStore(state => state.isOpen);
  const kind = useArtifactViewerStore(state => state.kind);
  const file = useArtifactViewerStore(state => state.file);
  const closeArtifactViewer = useArtifactViewerStore(state => state.closeArtifactViewer);
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const focusRef = useRef(null);
  const syncFocus = useMacModalFocus(focusRef, isOpen && !!file, closeArtifactViewer);
  const [loadedDocument, setLoadedDocument] = useState(null);
  const onSourceLoaded = useCallback((sourceFile, source) => setLoadedDocument({ file: sourceFile, source }), []);
  const documentSource = loadedDocument?.file === file ? loadedDocument.source : null;
  const prepareDownload = useCallback(options => prepareArtifactDownload(file, kind, documentSource, options), [file, kind, documentSource]);

  const styles = useMemo(() => StyleSheet.create({
    container: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      backgroundColor: colors.solid_color_primary,
      paddingTop: getWindowOverlayTopInset(insets.top),
      paddingBottom: insets.bottom,
      paddingLeft: insets.left,
      paddingRight: insets.right,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
      ...(Platform.OS === 'macos' ? {
        height: 48,
        paddingVertical: 0,
        backgroundColor: colors.solid_color_block,
      } : {}),
    },
    iconButton: {
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 18,
    },
    titleBlock: { flex: 1, minWidth: 0, gap: 1 },
    openButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      paddingHorizontal: 12,
      paddingVertical: 9,
      borderRadius: 8,
      backgroundColor: colors.solid_color_secondary,
    },
    body: { flex: 1 },
  }), [colors, insets]);

  // Without this, Android's back button pops the screen underneath while the
  // viewer stays on top of whatever it lands on.
  useEffect(() => {
    if (!isOpen) return undefined;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      closeArtifactViewer();
      return true;
    });
    return () => subscription.remove();
  }, [isOpen, closeArtifactViewer]);

  if (!isOpen || !file) return null;

  if (kind === 'video-project') {
    const VideoProjectPreviewPanel = require('./VideoProjectPreviewPanel').default;
    return <View ref={focusRef} collapsable={false} onLayout={syncFocus} style={styles.container}
      {...(Platform.OS === 'macos' ? { focusable: true, enableFocusRing: false } : {})}>
      <VideoProjectPreviewPanel {...file} onClose={closeArtifactViewer} />
    </View>;
  }

  if (kind === 'video') return <View ref={focusRef} collapsable={false} onLayout={syncFocus}
    style={styles.container} {...(Platform.OS === 'macos' ? { focusable: true, enableFocusRing: false } : {})}>
    <VideoPreviewPanel file={file} onClose={closeArtifactViewer} /></View>;

  const labels = KIND_LABELS[kind] || KIND_LABELS.presentation;
  const Viewer = KIND_VIEWERS[kind] || SlideDeckViewer;
  const title = file?.parameters?.title || file?.name || labels.fallbackTitle;
  const changeStats = kind === 'code' ? codeChangeStats(file) : null;
  // A structured preview can be newer than its CDN payload (or be internal
  // document JSON). Its Mac handoff goes through Save's format exporter.
  const canOpenOriginal = Platform.OS !== 'macos' || !['document', 'spreadsheet', 'presentation'].includes(kind);

  return (
    <View ref={focusRef} collapsable={false} onLayout={syncFocus} style={styles.container}
      {...(Platform.OS === 'macos' ? { focusable: true, enableFocusRing: false } : {})}>
      <View style={styles.header}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close preview"
          onPress={closeArtifactViewer}
          style={styles.iconButton}
        >
          <Icon name="xmark" size={18} color={colors.text_color_primary} />
        </Pressable>

        <View style={styles.titleBlock}>
          <Text weight="semibold" numberOfLines={1}>{title}</Text>
          <Text size="xs" color="tertiary" numberOfLines={1}>
            {changeStats
              ? `Changes  +${changeStats.added}  −${changeStats.removed}`
              : (labels.subtitle || 'Preview · ask in chat to make changes')}
          </Text>
        </View>

        {Platform.OS === 'macos' && <ArtifactDownloadButton
          key={`${kind}:${file.id || file._id || file.cdnPath || file.url || file.uri}`}
          file={file} prepareFile={prepareDownload} disabled={kind === 'document' && !documentSource}
          style={styles.openButton} />}

        {canOpenOriginal && <Pressable
          accessibilityRole="button"
          accessibilityLabel={labels.open}
          onPress={() => openFileExternally(file)}
          style={styles.openButton}
        >
          <Icon name="arrow-up-right-from-square" size={13} color={colors.text_color_primary} />
          <Text size="s">Open</Text>
        </Pressable>}
      </View>

      <View style={styles.body}>
        <Viewer file={file} onSourceLoaded={kind === 'document' ? onSourceLoaded : undefined}
          onFileSaved={kind === 'code' ? nextFile => useArtifactViewerStore.getState().openArtifactViewer('code', nextFile) : undefined} />
      </View>
    </View>
  );
}
